import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Minus, Plus } from "lucide-react";
import { FloorPlanZone } from "@/hooks/useFloorPlanZones";

interface ZoneManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  zone: FloorPlanZone | null;
  onSave: (zone: Partial<FloorPlanZone>) => void;
  isLoading?: boolean;
}

const PRESET_COLORS = [
  "#6366f1", // indigo
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#ef4444", // red
  "#f97316", // orange
  "#eab308", // yellow
  "#22c55e", // green
  "#14b8a6", // teal
  "#06b6d4", // cyan
  "#3b82f6", // blue
];

export const ZoneManagementDialog = ({
  open,
  onOpenChange,
  zone,
  onSave,
  isLoading,
}: ZoneManagementDialogProps) => {
  const [name, setName] = useState("");
  const [color, setColor] = useState("#6366f1");
  const [gridCols, setGridCols] = useState(12);
  const [gridRows, setGridRows] = useState(8);

  useEffect(() => {
    if (zone) {
      setName(zone.name);
      setColor(zone.color);
      setGridCols(zone.grid_cols);
      setGridRows(zone.grid_rows);
    } else {
      setName("");
      setColor("#6366f1");
      setGridCols(12);
      setGridRows(8);
    }
  }, [zone, open]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name,
      color,
      grid_cols: gridCols,
      grid_rows: gridRows,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {zone ? "Modifier la zone" : "Nouvelle zone"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="zone-name">Nom de la zone</Label>
            <Input
              id="zone-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Terrasse, Salle principale, Étage..."
              required
            />
          </div>

          <div className="space-y-2">
            <Label>Couleur</Label>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    color === c
                      ? "border-foreground scale-110"
                      : "border-transparent hover:scale-105"
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Colonnes de grille</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  onClick={() => setGridCols(Math.max(6, gridCols - 1))}
                  disabled={gridCols <= 6}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-2xl font-semibold min-w-[2.5rem] text-center">{gridCols}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  onClick={() => setGridCols(Math.min(30, gridCols + 1))}
                  disabled={gridCols >= 30}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Lignes de grille</Label>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  onClick={() => setGridRows(Math.max(4, gridRows - 1))}
                  disabled={gridRows <= 4}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-2xl font-semibold min-w-[2.5rem] text-center">{gridRows}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="h-10 w-10"
                  onClick={() => setGridRows(Math.min(20, gridRows + 1))}
                  disabled={gridRows >= 20}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>

          <p className="text-sm text-muted-foreground">
            La grille peut contenir jusqu'à {gridCols * gridRows} cellules.
            Augmentez ces valeurs pour plus de tables.
          </p>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={isLoading || !name.trim()}>
              {isLoading ? "Enregistrement..." : zone ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
